/**
 * TaskDetailsContent — Custom Details tab content for task entities.
 *
 * Replaces the flat key-value dump with:
 * - Priority visual banner (CRITICAL = red accent)
 * - 6 collapsible field sections (Accordion)
 * - Empty field hiding with "Show all" toggle
 * - Relative timestamps ("2d ago") with hover for full ISO
 * - Workflow progress indicator ("Task 3 of 12")
 *
 * DUMB FRONTEND: All data from TaskFieldsFragment, no assumptions.
 */

import { useState } from 'react';
import {
  Accordion,
  Badge,
  Box,
  Group,
  Progress,
  Stack,
  Switch,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconUser,
  IconClock,
  IconGitBranch,
  IconSettings,
  IconActivity,
} from '@tabler/icons-react';
import { GqlTaskPriority, type TaskFieldsFragment } from '@/generated/graphql';
import { FieldValue } from '@/components/RecordDetailDrawer';
import type { TaskSiblingInfo } from './useTaskSiblings';
import { SLACountdown } from './SLACountdown';

// ============================================================================
// PRIORITY BANNER
// ============================================================================

const PRIORITY_BANNER: Record<string, { bg: string; color: string; label: string }> = {
  [GqlTaskPriority.CRITICAL]: { bg: 'var(--mantine-color-red-8)', color: 'white', label: 'CRITICAL PRIORITY' },
  [GqlTaskPriority.HIGH]: { bg: 'var(--mantine-color-orange-7)', color: 'white', label: 'HIGH PRIORITY' },
  [GqlTaskPriority.NORMAL]: { bg: 'var(--mantine-color-blue-8)', color: 'white', label: 'NORMAL PRIORITY' },
  [GqlTaskPriority.LOW]: { bg: 'var(--mantine-color-gray-6)', color: 'white', label: 'LOW PRIORITY' },
};

// ============================================================================
// UTILITIES
// ============================================================================

/** Format relative time — extracted from TaskCard pattern */
export function timeAgo(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** Format milliseconds to human-readable duration */
function formatDuration(ms: string | null | undefined): string | null {
  if (!ms) return null;
  const minutes = Math.round(parseInt(ms, 10) / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
}

/** Check if a value is "empty" (null, undefined, empty array, empty object) */
function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && Object.keys(value as object).length === 0) return true;
  return false;
}

// ============================================================================
// FIELD ROW COMPONENT
// ============================================================================

interface FieldRowProps {
  label: string;
  value: unknown;
  /** Field key for auto-resolution (actor names, constellation IDs) */
  fieldKey?: string;
  /** Render timestamp as relative time with hover */
  isTimestamp?: boolean;
  /** Render as formatted duration */
  isDuration?: boolean;
}

function FieldRow({ label, value, fieldKey, isTimestamp, isDuration }: FieldRowProps) {
  if (isTimestamp && typeof value === 'string') {
    const relative = timeAgo(value);
    return (
      <Table.Tr>
        <Table.Td fw={600} w={160} style={{ textTransform: 'capitalize' }}>
          {label}
        </Table.Td>
        <Table.Td>
          <Tooltip label={new Date(value).toLocaleString()} position="top-start">
            <Text size="sm" style={{ cursor: 'help' }}>{relative}</Text>
          </Tooltip>
        </Table.Td>
      </Table.Tr>
    );
  }

  if (isDuration && typeof value === 'string') {
    const formatted = formatDuration(value);
    return (
      <Table.Tr>
        <Table.Td fw={600} w={160} style={{ textTransform: 'capitalize' }}>
          {label}
        </Table.Td>
        <Table.Td>
          <Text size="sm">{formatted ?? '—'}</Text>
        </Table.Td>
      </Table.Tr>
    );
  }

  return (
    <Table.Tr>
      <Table.Td fw={600} w={160} style={{ textTransform: 'capitalize' }}>
        {label}
      </Table.Td>
      <Table.Td>
        <FieldValue value={value} fieldKey={fieldKey} />
      </Table.Td>
    </Table.Tr>
  );
}

// ============================================================================
// SECTION DEFINITIONS
// ============================================================================

interface SectionField {
  key: string;
  label: string;
  isTimestamp?: boolean;
  isDuration?: boolean;
}

interface SectionDef {
  id: string;
  label: string;
  icon: React.ReactNode;
  fields: SectionField[];
  /** Default collapsed state */
  defaultClosed?: boolean;
}

const SECTIONS: SectionDef[] = [
  {
    id: 'assignment',
    label: 'Assignment',
    icon: <IconUser size={16} />,
    fields: [
      { key: 'assignedActor', label: 'Assigned To'},
      { key: 'assignedAt', label: 'Assigned At', isTimestamp: true },
      { key: 'createdBy', label: 'Created By'},
      { key: 'queueId', label: 'Queue' },
    ],
  },
  {
    id: 'progress',
    label: 'Progress',
    icon: <IconActivity size={16} />,
    fields: [
      { key: 'status', label: 'Status' },
      { key: 'taskType', label: 'Task Type' },
      { key: 'progressPercent', label: 'Progress' },
      { key: 'sequenceOrder', label: 'Sequence Order' },
    ],
  },
  {
    id: 'timing',
    label: 'Timing',
    icon: <IconClock size={16} />,
    fields: [
      { key: 'createdAt', label: 'Created', isTimestamp: true },
      { key: 'startedAt', label: 'Started', isTimestamp: true },
      { key: 'completedAt', label: 'Completed', isTimestamp: true },
      { key: 'updatedAt', label: 'Updated', isTimestamp: true },
      { key: 'estimatedDurationMs', label: 'Estimated Duration', isDuration: true },
      { key: 'durationMs', label: 'Actual Duration', isDuration: true },
    ],
  },
  {
    id: 'dependencies',
    label: 'Dependencies',
    icon: <IconGitBranch size={16} />,
    fields: [
      { key: 'dependencies', label: 'Depends On' },
      { key: 'dependents', label: 'Depended By' },
      { key: 'constraintIds', label: 'Constraints' },
      { key: 'validationIds', label: 'Validations' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    icon: <IconSettings size={16} />,
    defaultClosed: true,
    fields: [
      { key: 'id', label: 'Task ID' },
      { key: 'parentId', label: 'Parent ID' },
      { key: 'tenantId', label: 'Tenant' },
      { key: 'workflowInstanceId', label: 'Workflow Instance' },
      { key: 'version', label: 'Version' },
      { key: 'retryCount', label: 'Retry Count' },
      { key: 'maxRetries', label: 'Max Retries' },
      { key: 'failureStrategy', label: 'Failure Strategy' },
      { key: 'errorMessage', label: 'Error Message' },
      { key: 'slaId', label: 'SLA' },
      { key: 'metadata', label: 'Metadata' },
    ],
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

interface TaskDetailsContentProps {
  task: TaskFieldsFragment;
  siblingInfo: TaskSiblingInfo;
}

export function TaskDetailsContent({ task, siblingInfo }: TaskDetailsContentProps) {
  const [showAll, setShowAll] = useState(false);

  const priorityBanner = PRIORITY_BANNER[task.priority];

  // Default open sections (all except those marked defaultClosed)
  const defaultOpen = SECTIONS
    .filter(s => !s.defaultClosed)
    .map(s => s.id);

  return (
    <Stack gap="sm">
      {/* Priority banner */}
      {priorityBanner && (
        <Box
          py={6}
          px="sm"
          style={{
            background: priorityBanner.bg,
            borderRadius: 'var(--mantine-radius-sm)',
          }}
        >
          <Text size="xs" fw={700} c={priorityBanner.color} ta="center">
            {priorityBanner.label}
          </Text>
        </Box>
      )}

      {/* SLA countdown (renders nothing if no estimatedDurationMs) */}
      <SLACountdown task={task} />

      {/* Workflow progress indicator */}
      {siblingInfo.total > 0 && (
        <Box px="xs">
          <Group justify="space-between" mb={4}>
            <Text size="xs" c="dimmed">
              Task {siblingInfo.position} of {siblingInfo.total}
            </Text>
            <Text size="xs" c="dimmed">
              {siblingInfo.completedCount} completed
            </Text>
          </Group>
          <Progress
            value={(siblingInfo.completedCount / siblingInfo.total) * 100}
            size="xs"
            color="green"
          />
        </Box>
      )}

      {/* Task name + description header */}
      <Box px="xs">
        <Text fw={600} size="md">{task.name}</Text>
        {task.description && (
          <Text size="sm" c="dimmed" mt={2}>{task.description}</Text>
        )}
      </Box>

      {/* Show all fields toggle */}
      <Group px="xs" justify="flex-end">
        <Switch
          label="Show all fields"
          size="xs"
          checked={showAll}
          onChange={(e) => setShowAll(e.currentTarget.checked)}
        />
      </Group>

      {/* Collapsible sections */}
      <Accordion
        multiple
        defaultValue={defaultOpen}
        variant="separated"
        styles={{
          control: { padding: 'var(--mantine-spacing-xs)' },
          panel: { padding: 0 },
        }}
      >
        {SECTIONS.map(section => {
          const taskRecord = task as unknown as Record<string, unknown>;
          const visibleFields = showAll
            ? section.fields
            : section.fields.filter(f => !isEmpty(taskRecord[f.key]));

          // Hide entire section if all fields are empty and not showing all
          if (visibleFields.length === 0) return null;

          return (
            <Accordion.Item key={section.id} value={section.id}>
              <Accordion.Control icon={section.icon}>
                <Group gap="xs">
                  <Text size="sm" fw={500}>{section.label}</Text>
                  <Badge size="xs" variant="light" color="gray">
                    {visibleFields.length}
                  </Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Table
                  striped
                  highlightOnHover
                  withTableBorder={false}
                  aria-label={`${section.label} fields`}
                >
                  <Table.Tbody>
                    {visibleFields.map(field => (
                      <FieldRow
                        key={field.key}
                        label={field.label}
                        value={taskRecord[field.key]}
                        fieldKey={field.key}
                        isTimestamp={field.isTimestamp}
                        isDuration={field.isDuration}
                      />
                    ))}
                  </Table.Tbody>
                </Table>
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </Stack>
  );
}
